import { unzip } from 'fflate'

import type { PreviewItem } from '../stores/printPreviewStore'

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

const mimeFor = (filename: string) =>
  MIME_BY_EXTENSION[filename.split('.').pop()?.toLowerCase() ?? ''] ??
  'application/octet-stream'

/**
 * Nazwa pliku raportu dla pojedynczego kierowcy zaczyna sie od jego imienia i
 * nazwiska ze spacjami zamienionymi na podkreslenia (BaseReport.GetFileName).
 * Po tym rozpoznajemy, do ktorego folderu w paczce ZIP trafi dokument.
 */
export const resolveFolder = (
  filename: string,
  subjects: string[],
): string | undefined => {
  if (subjects.length < 2) return undefined

  const normalized = filename.toLowerCase()

  // Nazwy potrafia byc swoimi prefiksami ("Nowak Jan" vs "Nowak Jan Adam"),
  // wiec wygrywa najdluzsze dopasowanie.
  return subjects
    .filter((subject) =>
      normalized.startsWith(`${subject.replace(/\s+/g, '_').toLowerCase()}_`),
    )
    .sort((a, b) => b.length - a.length)[0]
}

/** Rozpakowuje paczke zwrocona przez zadanie wydruku na pojedyncze dokumenty. */
export const extractBatchFiles = async (
  zipBlobUrl: string,
  subjects: string[],
): Promise<PreviewItem[]> => {
  const buffer = await fetch(zipBlobUrl).then((r) => r.arrayBuffer())

  const entries = await new Promise<Record<string, Uint8Array>>(
    (resolve, reject) => {
      unzip(new Uint8Array(buffer), (err, data) =>
        err ? reject(err) : resolve(data),
      )
    },
  )

  return Object.entries(entries)
    .filter(([, data]) => data.length > 0)
    .map(([path, data]) => {
      const filename = path.split('/').pop() as string
      const blob = new Blob([data], { type: mimeFor(filename) })
      return {
        blobUrl: URL.createObjectURL(blob),
        filename,
        folder: resolveFolder(filename, subjects),
      }
    })
    .sort((a, b) =>
      `${a.folder ?? ''}${a.filename}`.localeCompare(
        `${b.folder ?? ''}${b.filename}`,
      ),
    )
}
